import { useEffect, useRef, useState } from 'react';
import { useSpring, animated } from '@react-spring/web';
import { cn } from '../../lib/utils';

// ── Types ─────────────────────────────────────────────────────────────────────

export type StatusChipVariant = 'default' | 'warning' | 'success' | 'danger';

export interface StatusChipProps {
  /** Color variant — maps to a semantic status */
  variant?: StatusChipVariant;
  /** Icon rendered left of the label (e.g. <Github className="h-3 w-3" />) */
  icon: React.ReactNode;
  /** Chip label text — truncated beyond max-w */
  label: string;
  /** Width of the popover panel in px. Default 260 */
  popoverWidth?: number;
  /** Popover content rendered below the chip on click */
  children?: React.ReactNode;
  className?: string;
}

// ── Variant styles ────────────────────────────────────────────────────────────

const VARIANTS: Record<StatusChipVariant, { chip: string; dot: string }> = {
  default: {
    chip: 'bg-muted/60 text-muted-foreground border-border/60 hover:bg-muted',
    dot:  'bg-muted-foreground/50',
  },
  warning: {
    chip: 'bg-amber-500/10 text-amber-400 border-amber-500/25 hover:bg-amber-500/15',
    dot:  'bg-amber-400',
  },
  success: {
    chip: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/25 hover:bg-emerald-500/15',
    dot:  'bg-emerald-400',
  },
  danger: {
    chip: 'bg-red-500/10 text-red-400 border-red-500/25 hover:bg-red-500/15',
    dot:  'bg-red-400',
  },
};

// ── Component ─────────────────────────────────────────────────────────────────

export function StatusChip({
  variant = 'default',
  icon,
  label,
  popoverWidth = 260,
  children,
  className,
}: StatusChipProps) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const { chip, dot } = VARIANTS[variant];

  // Click-outside to close
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Spring: animate popover in/out
  const spring = useSpring({
    opacity:   open ? 1 : 0,
    scale:     open ? 1 : 0.95,
    translateY: open ? 0 : -6,
    config: { tension: 280, friction: 24 },
  });

  return (
    <div ref={containerRef} className="relative">
      {/* Chip trigger */}
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        className={cn(
          'flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-medium transition-colors select-none',
          chip,
          open && 'brightness-110',
          className,
        )}
      >
        {icon}
        <span className="max-w-[160px] truncate">{label}</span>
        <span className={cn('h-1.5 w-1.5 rounded-full shrink-0', dot)} />
      </button>

      {/* Animated popover */}
      {children && (
        <animated.div
          style={{
            opacity:   spring.opacity,
            scale:     spring.scale,
            translateY: spring.translateY,
            width:     popoverWidth,
            pointerEvents: open ? 'auto' : 'none',
          }}
          className="absolute right-0 top-full mt-2 z-50 rounded-lg border border-border bg-popover shadow-lg overflow-hidden origin-top-right"
        >
          {children}
        </animated.div>
      )}
    </div>
  );
}
